import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, ShoppingCart, CheckCircle, TrendingUp, CreditCard, AlertCircle, CheckCircle2, Zap, Shield } from 'lucide-react';
import { toast } from 'sonner';
import apiClient, { getAuthToken } from '@/utils/api';
import { jwtDecode } from 'jwt-decode';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  let userType = 'employee';
  const token = getAuthToken();
  if (token) {
    try {
      userType = jwtDecode(token).user_type || 'employee';
    } catch(e) {
      // M-4 fix: clear invalid token and redirect
      localStorage.removeItem('admin_token');
      sessionStorage.removeItem('admin_token');
    }
  }

  const [stats, setStats] = useState({
    totalTables: 0,
    occupiedTables: 0,
    pendingOrders: 0,
    completedOrders: 0,
    dailyRevenue: 0,
    monthlyRevenue: 0,
    dailyCashRevenue: 0,
    dailyCardRevenue: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!getAuthToken()) {
      navigate('/login');
      return;
    }
    
    // Check payment status from URL
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      toast.success('Ödeme başarılı! Aboneliğiniz aktifleştirildi.');
      window.history.replaceState({}, '', '/admin/dashboard');
    } else if (paymentStatus === 'cancelled') {
      toast.error('Ödeme iptal edildi.');
      window.history.replaceState({}, '', '/admin/dashboard');
    }
    
    fetchDashboardData();
    fetchSubscriptionStatus();
  }, [navigate, searchParams]);

  const fetchDashboardData = async () => {
    try {
      const [tablesRes, ordersRes, revenueRes] = await Promise.all([
        apiClient.get('/tables'),
        apiClient.get('/orders'),
        apiClient.get('/revenue/stats')
      ]);

      const tables = tablesRes.data;
      const orders = ordersRes.data;
      const revenue = revenueRes.data;

      setStats({
        totalTables: tables.length,
        occupiedTables: orders.filter(o => o.status !== 'completed').length,
        pendingOrders: orders.filter(o => o.status === 'pending').length,
        completedOrders: orders.filter(o => o.status === 'completed').length,
        dailyRevenue: revenue.daily_revenue || 0,
        monthlyRevenue: revenue.monthly_revenue || 0,
        dailyCashRevenue: revenue.daily_cash_revenue || 0,
        dailyCardRevenue: revenue.daily_card_revenue || 0
      });

      setRecentOrders(orders.slice(0, 5));
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await apiClient.get('/subscription/status');
      setSubscription(response.data);
    } catch (error) {
      console.error('Subscription status error:', error);
    }
  };

  const handlePayment = async () => {
    if (!selectedMonths || selectedMonths < 1) {
      toast.error('Lütfen geçerli bir süre seçin');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/payment/iyzico/initialize', null, {
        params: { months: selectedMonths }
      });
      
      const checkoutUrl = response.data.checkout_url;
      try {
        const urlObj = new URL(checkoutUrl);
        if (urlObj.hostname === 'www.iyzipay.com' || urlObj.hostname.endsWith('.iyzipay.com')) {
          window.location.href = checkoutUrl;
        } else {
          toast.error('Geçersiz ödeme bağlantısı.');
          setLoading(false);
        }
      } catch (e) {
        toast.error('Geçersiz ödeme bağlantısı formatı.');
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
      toast.error('Ödeme başlatılamadı. Lütfen tekrar deneyin.');
      setLoading(false);
    }
  };

  const statCards = [
    { icon: Users, label: 'Toplam Masa', value: stats.totalTables, color: 'text-blue-400' },
    { icon: ShoppingCart, label: 'Aktif Sipariş', value: stats.occupiedTables, color: 'text-orange-400' },
    { icon: CheckCircle, label: 'Tamamlanan', value: stats.completedOrders, color: 'text-emerald-400' },
    { icon: TrendingUp, label: 'Günlük Gelir', value: `${stats.dailyRevenue.toFixed(2)} ₺`, color: 'text-green-400' },
    { icon: CreditCard, label: 'Günlük Kart', value: `${stats.dailyCardRevenue.toFixed(2)} ₺`, color: 'text-blue-500' },
    { icon: Zap, label: 'Günlük Nakit', value: `${stats.dailyCashRevenue.toFixed(2)} ₺`, color: 'text-orange-500' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Hoş geldiniz, {localStorage.getItem('business_name') || localStorage.getItem('user_name')}</p>
        </div>

        {/* Subscription Status Card - Only for owner */}
        {userType === 'owner' && subscription && (
          <Card className={`backdrop-blur-sm p-6 border-2 ${
            subscription.subscription_status === 'trial' ? 'bg-yellow-900/20 border-yellow-600' :
            subscription.subscription_status === 'active' ? 'bg-green-900/20 border-green-600' :
            'bg-red-900/20 border-red-600'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <AlertCircle className={
                  subscription.subscription_status === 'trial' ? 'text-yellow-400' :
                  subscription.subscription_status === 'active' ? 'text-green-400' :
                  'text-red-400'
                } size={32} />
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                    {subscription.subscription_status === 'trial' ? '7 Günlük Deneme Süresi' :
                     subscription.subscription_status === 'active' ? 'Aktif Abonelik' :
                     'Abonelik Süresi Doldu'}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">
                    {subscription.subscription_end_date ? 
                      `Bitiş: ${new Date(subscription.subscription_end_date).toLocaleDateString('tr-TR')}` :
                      'Lütfen abonelik satın alın'}
                  </p>
                </div>
              </div>
              <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-orange-500 hover:bg-orange-600">
                    <CreditCard size={16} className="mr-2" />
                    Abonelik Satın Al
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 max-w-4xl p-0 overflow-hidden">
                  <div className="p-8 text-center bg-gradient-to-br from-slate-900 via-slate-900 to-orange-950/20">
                    <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Aboneliğinizi Uzatın
                    </h2>
                    <p className="text-slate-600 dark:text-slate-400 max-w-xl mx-auto">
                      Sistemi kesintisiz kullanmaya devam etmek için size en uygun planı seçin. Iyzico güvencesiyle anında aktifleştirin.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-6 bg-slate-50 dark:bg-slate-950">
                    {[
                      { m: 1, t: 'Aylık Plan', p: 300, badge: null, color: 'from-slate-800 to-slate-900', border: 'border-slate-200 dark:border-slate-800' },
                      { m: 3, t: '3 Aylık Plan', p: 850, badge: 'Popüler', color: 'from-orange-500/10 to-orange-900/10', border: 'border-orange-500/50' },
                      { m: 6, t: '6 Aylık Plan', p: 1600, badge: null, color: 'from-blue-500/10 to-blue-900/10', border: 'border-blue-500/30' },
                      { m: 12, t: 'Yıllık Plan', p: 3100, badge: 'En Avantajlı', color: 'from-emerald-500/10 to-emerald-900/10', border: 'border-emerald-500/50' }
                    ].map((plan) => (
                      <div 
                        key={plan.m}
                        onClick={() => setSelectedMonths(plan.m)}
                        className={`relative rounded-xl border-2 p-5 cursor-pointer transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl bg-gradient-to-br ${plan.color} ${selectedMonths === plan.m ? plan.border + ' shadow-lg shadow-' + plan.border.split('-')[1] : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:border-slate-700'}`}
                      >
                        {plan.badge && (
                          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-lg">
                            <Zap size={12} /> {plan.badge}
                          </div>
                        )}
                        
                        <div className="text-center mt-2 mb-4">
                          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">{plan.t}</h3>
                          <div className="mt-2 flex items-baseline justify-center gap-1">
                            <span className="text-3xl font-extrabold text-slate-900 dark:text-white">{plan.p}</span>
                            <span className="text-slate-600 dark:text-slate-400 font-medium">₺</span>
                          </div>
                        </div>
                        
                        <ul className="space-y-3 mb-6 text-sm text-slate-700 dark:text-slate-300">
                          <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-green-500 shrink-0 mt-0.5" /> Tüm POS Özellikleri</li>
                          <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-green-500 shrink-0 mt-0.5" /> Sınırsız Sipariş</li>
                          <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-green-500 shrink-0 mt-0.5" /> {plan.m >= 6 ? '7/24 VIP Destek' : 'Standart Destek'}</li>
                        </ul>
                        
                        <div className={`w-full py-2 rounded-lg text-center text-sm font-bold transition-colors ${selectedMonths === plan.m ? 'bg-orange-500 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'}`}>
                          {selectedMonths === plan.m ? 'Seçildi' : 'Seç'}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="p-6 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-slate-500 text-xs flex items-center gap-2">
                      <Shield className="w-4 h-4 text-emerald-500" />
                      Iyzico 256-bit SSL Güvenli Ödeme Altyapısı
                    </p>
                    <Button
                      onClick={handlePayment}
                      disabled={loading}
                      className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 px-8 py-6 text-lg font-bold shadow-lg shadow-orange-500/20"
                    >
                      {loading ? 'Yönlendiriliyor...' : `${
                        selectedMonths === 1 ? 300 :
                        selectedMonths === 3 ? 850 :
                        selectedMonths === 6 ? 1600 :
                        3100
                      } ₺ ile Aboneliği Başlat`}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {statCards.map((stat, index) => (
            <Card
              key={index}
              data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
              className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 backdrop-blur-sm p-6 hover:border-orange-500/30 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">{stat.label}</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-slate-100 mt-2">{stat.value}</p>
                </div>
                <stat.icon className={`${stat.color}`} size={32} />
              </div>
            </Card>
          ))}
        </div>

        <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 backdrop-blur-sm p-6">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Son Siparişler
          </h2>
          <div className="space-y-3">
            {recentOrders.length === 0 ? (
              <p className="text-slate-600 dark:text-slate-400 text-center py-8">Henüz sipariş yok</p>
            ) : (
              recentOrders.map((order) => (
                <div
                  key={order.id}
                  data-testid={`order-item-${order.id}`}
                  className="flex items-center justify-between p-4 bg-slate-100 dark:bg-slate-800/50 rounded-lg hover:bg-slate-100 dark:bg-slate-800 transition-colors"
                >
                  <div>
                    <p className="text-slate-800 dark:text-slate-200 font-medium">Masa {order.table_number}</p>
                    <p className="text-slate-600 dark:text-slate-400 text-sm">{order.items.length} ürün</p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-800 dark:text-slate-200 font-mono font-bold">{order.total_amount.toFixed(2)} ₺</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                      order.status === 'preparing' ? 'bg-blue-500/20 text-blue-400' :
                      order.status === 'ready' ? 'bg-green-500/20 text-green-400' :
                      'bg-slate-500/20 text-slate-600 dark:text-slate-400'
                    }`}>
                      {order.status === 'pending' ? 'Bekliyor' :
                       order.status === 'preparing' ? 'Hazırlanıyor' :
                       order.status === 'ready' ? 'Hazır' : 'Tamamlandı'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default DashboardPage;